/**
 * useTempoPolicy -- React Query wrappers over the member-
 * liquidity-engine policy chassis (overdraft-coverage arc OC-3, decision
 * D7: MemberLiquidityPolicy is a peer policy domain in the FI risk
 * console).
 *
 * Server contract (tempo.openapi.yaml, all typed via
 * the memberLiquidityClient openapi-fetch client):
 *
 *   GET  /api/v1/tempo/policy                 effective TEMPO version
 *   PUT  /api/v1/tempo/policy                 publish a new immutable version
 *   GET  /api/v1/tempo/delivery-mode          the tenant's LIVE delivery mode
 *   PUT  /api/v1/tempo/delivery-mode          set the live mode (GH #2744)
 *   GET  /api/v1/tempo/policy/pool-appetite   effective RECEIVABLES_POOL_APPETITE version
 *   PUT  /api/v1/tempo/policy/pool-appetite   publish a new immutable version
 *   GET  /api/v1/tempo/policy/versions        cursor-paginated history (newest first)
 *   GET  /api/v1/tempo/policy/evidence        examiner export (versions + integrity hash)
 *
 * TWO SWITCHES, DELIBERATELY SEPARATE (GH #2744). Publishing a version and
 * turning the program on are different acts with different endpoints. The
 * policy PUT stamps the mode in force at publish time as an audit fact; the
 * delivery-mode PUT is what the engine actually reads. Before the split, a
 * single `enabled` bit on the version did both, so an FI revising a dial
 * re-asserted the on/off state as a side effect and turning the program off
 * meant publishing a version that changed no dials.
 *
 * The delivery-mode PUT names its tenant EXPLICITLY in the body. A platform-admin
 * session carries no tenantId claim and the server resolves it to apex, so a
 * body-less write would target apex while the operator believed they were
 * changing the FI on screen. useTenantCacheKey's 'apex' literal below is the same
 * fact showing up in the cache layer.
 *
 * 404 handling: the WHOLE surface is gated server-side on
 * Institution.enableOverdraftPreview (404-on-OFF stealth), and each policy GET
 * also 404s when no version is EFFECTIVE -- since GH #2745 that includes the
 * case where versions exist but every one is future-dated. All of these resolve
 * to `null` here; the pages render a "no policy published yet" state, and no
 * error (the nav is already flag-gated, so a flag-off 404 is
 * unreachable in practice except via deep link).
 *
 * 422 handling: a semantically incoherent PUT returns
 * POLICY_SEMANTIC_VALIDATION_FAILED with reason-coded details
 * (`TIER_CAPS_NON_MONOTONIC: ...`). Mutations rethrow an Error carrying
 * the details so the form can render them as an Alert.
 *
 * Cache keys mirror the useInstitutionFlags impersonation contract: the
 * 'apex' literal is a CLIENT-SIDE cache-namespace label for platform-admin
 * sessions (no tenantId claim); the backend resolves the tenant from the
 * JWT via resolveEffectiveTenantId.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from '@aaimplatform/institutional-types/tempo';
import { memberLiquidityClient } from '../api/client';
import { resolveOrgContext } from '../layouts/org-context';
import { useAuthStore } from '../stores/auth';

export type MemberLiquidityPolicy = components['schemas']['MemberLiquidityPolicy'];
export type MemberLiquidityPolicyInput = components['schemas']['MemberLiquidityPolicyInput'];
export type LiquidityPolicyParameters = components['schemas']['LiquidityPolicyParameters'];
export type LiquidityCureCapBasis = components['schemas']['LiquidityCureCapBasis'];
export type LiquidityDataLaneMode = components['schemas']['LiquidityDataLaneMode'];
export type PoolAppetitePolicy = components['schemas']['PoolAppetitePolicy'];
export type PoolAppetitePolicyInput = components['schemas']['PoolAppetitePolicyInput'];
export type PoolAppetiteParameters = components['schemas']['PoolAppetiteParameters'];
export type PolicyVersionPage = components['schemas']['PolicyVersionPage'];
export type PolicyVersionEntry = components['schemas']['PolicyVersionEntry'];
export type PolicyEvidenceExport = components['schemas']['PolicyEvidenceExport'];
export type PolicyDomain = components['schemas']['PolicyDomain'];
export type LimitsDistribution = components['schemas']['LimitsDistribution'];
export type LimitsDistributionBucket = components['schemas']['LimitsDistributionBucket'];
export type TempoDeliveryMode = components['schemas']['TempoDeliveryMode'];
export type TenantDeliveryMode = components['schemas']['TenantDeliveryMode'];

const POLICY_KEY = 'member-liquidity-policy';
const POOL_KEY = 'member-liquidity-pool-appetite';
const VERSIONS_KEY = 'member-liquidity-policy-versions';
const DELIVERY_MODE_KEY = 'tempo-delivery-mode';

/**
 * Normalize an openapi-fetch error body (spec ErrorResponse envelope, or
 * the flag gate's flat stealth body) into an Error whose optional
 * `details` carries the 422 reason-coded entries for the form Alert.
 */
function toApiError(error: unknown, response: Response): Error & {
  details?: { path?: string; issue?: string; message?: string }[];
  status?: number;
  code?: string;
} {
  const envelope = error as
    | { error?: { code?: string; message?: string; details?: { path?: string; issue?: string; message?: string }[] } | string; message?: string }
    | undefined;
  let message = `HTTP ${response.status}`;
  let details: { path?: string; issue?: string; message?: string }[] | undefined;
  let code: string | undefined;
  if (envelope && typeof envelope.error === 'object' && envelope.error) {
    message = envelope.error.message ?? message;
    details = envelope.error.details;
    code = envelope.error.code;
  } else if (envelope && typeof envelope.message === 'string') {
    message = envelope.message;
  }
  return Object.assign(new Error(message), { details, status: response.status, code });
}

/** Client-side cache-key namespace per the impersonation contract. */
function useTenantCacheKey(): string {
  const user = useAuthStore((s) => s.user);
  return user?.tenantId ?? 'apex';
}

/** Effective TEMPO policy; null until a first version is published (or flag off). */
export function useEffectiveLiquidityPolicy() {
  const tenantKey = useTenantCacheKey();
  return useQuery<MemberLiquidityPolicy | null, Error>({
    queryKey: [POLICY_KEY, tenantKey],
    queryFn: async () => {
      const { data, error, response } = await memberLiquidityClient.GET('/api/v1/tempo/policy');
      if (response.status === 404) return null;
      if (error || !data) throw toApiError(error, response);
      return data;
    },
    retry: false,
  });
}

/** A write refused before its PUT: the page and the session name different tenants. */
const TENANT_MISMATCH = 'TENANT_MISMATCH';
/** The PUT answered for a tenant other than the one the page loaded. */
export const PUBLISHED_FOR_OTHER_TENANT = 'PUBLISHED_FOR_OTHER_TENANT';

function publishRefusal(message: string, code: string): Error & { code: string } {
  return Object.assign(new Error(message), { code });
}

/**
 * Two spellings of one tenant id. A uuid reads the same in either letter
 * case: the impersonation mint keeps the case it was given, and the server
 * answers a read with the stored, lower-case id.
 */
function sameTenant(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * Publish a new immutable TEMPO version (PUT = versioned publish, never in-place edit).
 *
 * TENANT CHECK (GH #2939). MemberLiquidityPolicyInput carries no tenant, so the
 * PUT publishes for whatever tenant the session cookie resolves to when it
 * lands. Impersonation rotates one HttpOnly cookie pair shared by every tab, so
 * a second tab that changes institution retargets this tab's publish while this
 * screen still shows the first institution. The caller therefore names the
 * tenant it loaded:
 *
 *   expectedTenantId  the tenantId of the page's delivery-mode read. Null when
 *                     that read produced nothing; the publish is refused.
 *   dialsTenantId     the tenantId of the version the dials were hydrated from,
 *                     absent when no version exists. Refused when it differs
 *                     from expectedTenantId: the dials would land on another
 *                     institution.
 *
 * The page's own reads ride the shared cookie too, so a switch in another tab
 * before they ran makes every server answer agree on the other institution.
 * The hook therefore also compares expectedTenantId with the institution this
 * tab shows: the OrgSwitcher's current org, which resolveOrgContext derives
 * from this tab's auth store by the server resolver's rules (an impersonated
 * tenant, apex for a platform admin, else the FI user's tenant). Nothing
 * syncs the store across tabs, so it still names what this tab displays.
 *
 * Before the PUT the hook re-reads GET /delivery-mode, which resolves the
 * tenant from the cookie as the PUT does, and refuses on a mismatch. After the
 * PUT it compares published.tenantId and raises PUBLISHED_FOR_OTHER_TENANT on a
 * mismatch, so the response is neither cached under this page's key nor
 * reported as this institution's new version. A tab switch between the re-read
 * and the PUT still lands; only a server-side expected tenant on the write
 * closes that window.
 */
export function usePublishLiquidityPolicy() {
  const tenantKey = useTenantCacheKey();
  const user = useAuthStore((s) => s.user);
  const tabTenantId = resolveOrgContext(user, undefined).currentOrg.id;
  const queryClient = useQueryClient();
  return useMutation<
    MemberLiquidityPolicy,
    Error & { details?: { path?: string; issue?: string; message?: string }[]; code?: string },
    { input: MemberLiquidityPolicyInput; expectedTenantId: string | null; dialsTenantId?: string }
  >({
    mutationFn: async ({ input, expectedTenantId, dialsTenantId }) => {
      if (!expectedTenantId) {
        throw publishRefusal(
          'Publishing is refused: the delivery mode could not be read, so this page cannot confirm which institution it publishes for.',
          TENANT_MISMATCH,
        );
      }
      if (!sameTenant(expectedTenantId, tabTenantId)) {
        throw publishRefusal(
          `Publishing is refused: this tab shows tenant ${tabTenantId} and this page read tenant ${expectedTenantId}, so the session no longer acts for the institution this tab shows. Reload the page before publishing.`,
          TENANT_MISMATCH,
        );
      }
      if (dialsTenantId !== undefined && dialsTenantId !== expectedTenantId) {
        throw publishRefusal(
          `Publishing is refused: the dials on this page were read for tenant ${dialsTenantId} and the delivery mode for tenant ${expectedTenantId}. Reload the page before publishing.`,
          TENANT_MISMATCH,
        );
      }

      const current = await memberLiquidityClient.GET('/api/v1/tempo/delivery-mode');
      if (current.error || !current.data) {
        throw publishRefusal(
          `Publishing is refused: the institution this session acts for could not be re-read (${toApiError(current.error, current.response).message}).`,
          TENANT_MISMATCH,
        );
      }
      if (current.data.tenantId !== expectedTenantId) {
        throw publishRefusal(
          `Publishing is refused: this page loaded tenant ${expectedTenantId} and the session now acts for tenant ${current.data.tenantId}. Reload the page before publishing.`,
          TENANT_MISMATCH,
        );
      }

      const { data, error, response } = await memberLiquidityClient.PUT('/api/v1/tempo/policy', {
        body: input,
      });
      if (error || !data) throw toApiError(error, response);
      if (data.tenantId !== expectedTenantId) {
        throw publishRefusal(
          `Version ${data.policyVersion} was published for tenant ${data.tenantId}. This page loaded tenant ${expectedTenantId}; the session changed institution during the publish. Reload the page and check both institutions' version history.`,
          PUBLISHED_FOR_OTHER_TENANT,
        );
      }
      return data;
    },
    onSuccess: (published) => {
      queryClient.setQueryData([POLICY_KEY, tenantKey], published);
      void queryClient.invalidateQueries({ queryKey: [VERSIONS_KEY, tenantKey] });
    },
  });
}

/**
 * The tenant's LIVE delivery mode. Null on the flag-off stealth 404 and on
 * TENANT_NOT_FOUND, both of which the pages render as "unknown" rather than as
 * OFF: showing OFF for a mode the server would not tell us is the same class of
 * defect as rendering a failed money read as $0 (GH #2742).
 */
export function useTempoDeliveryMode() {
  const tenantKey = useTenantCacheKey();
  return useQuery<TenantDeliveryMode | null, Error>({
    queryKey: [DELIVERY_MODE_KEY, tenantKey],
    queryFn: async () => {
      const { data, error, response } = await memberLiquidityClient.GET(
        '/api/v1/tempo/delivery-mode',
      );
      if (response.status === 404) return null;
      if (error || !data) throw toApiError(error, response);
      return data;
    },
    retry: false,
  });
}

/**
 * Set the tenant's live delivery mode. The caller supplies the tenantId, which
 * the server compares against the authenticated tenant and rejects with 403
 * TENANT_MISMATCH on a mismatch it is not authorized for -- see the header note
 * on why this is not inferred from the session.
 *
 * TENANT CHECK (GH #2939). The page takes that tenantId from its own
 * delivery-mode read, which rides the cookie pair every tab shares. When a
 * second tab changed institution before the read ran, the read, the body and
 * the server's check all name the other institution, and the write would land
 * there while this tab's header still shows the first. The same happens in
 * one tab when a 15-minute impersonation token expires and the silent refresh
 * restores the operator's own session: the page then reads apex under the
 * institution's header. The hook therefore refuses, before the PUT, a tenantId
 * that differs from the institution this tab shows (the same comparison
 * usePublishLiquidityPolicy makes).
 */
export function useSetTempoDeliveryMode() {
  const tenantKey = useTenantCacheKey();
  const user = useAuthStore((s) => s.user);
  const tabTenantId = resolveOrgContext(user, undefined).currentOrg.id;
  const queryClient = useQueryClient();
  return useMutation<
    TenantDeliveryMode,
    Error & { status?: number; code?: string },
    { tenantId: string; deliveryMode: TempoDeliveryMode }
  >({
    mutationFn: async (input) => {
      if (!sameTenant(input.tenantId, tabTenantId)) {
        throw publishRefusal(
          `This tab shows tenant ${tabTenantId} and this page read the delivery mode of tenant ${input.tenantId}, so the session no longer acts for the institution this tab shows. Reload the page before changing the delivery mode.`,
          TENANT_MISMATCH,
        );
      }
      const { data, error, response } = await memberLiquidityClient.PUT(
        '/api/v1/tempo/delivery-mode',
        { body: input },
      );
      if (error || !data) throw toApiError(error, response);
      return data;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData([DELIVERY_MODE_KEY, tenantKey], updated);
    },
  });
}

/** Effective RECEIVABLES_POOL_APPETITE policy; null until first publish (or flag off). */
export function usePoolAppetitePolicy() {
  const tenantKey = useTenantCacheKey();
  return useQuery<PoolAppetitePolicy | null, Error>({
    queryKey: [POOL_KEY, tenantKey],
    queryFn: async () => {
      const { data, error, response } = await memberLiquidityClient.GET(
        '/api/v1/tempo/policy/pool-appetite',
      );
      if (response.status === 404) return null;
      if (error || !data) throw toApiError(error, response);
      return data;
    },
    retry: false,
  });
}

/** Publish a new immutable RECEIVABLES_POOL_APPETITE version. */
export function usePublishPoolAppetite() {
  const tenantKey = useTenantCacheKey();
  const queryClient = useQueryClient();
  return useMutation<PoolAppetitePolicy, Error & { details?: { path?: string; issue?: string; message?: string }[] }, PoolAppetitePolicyInput>({
    mutationFn: async (input: PoolAppetitePolicyInput) => {
      const { data, error, response } = await memberLiquidityClient.PUT(
        '/api/v1/tempo/policy/pool-appetite',
        { body: input },
      );
      if (error || !data) throw toApiError(error, response);
      return data;
    },
    onSuccess: (published) => {
      queryClient.setQueryData([POOL_KEY, tenantKey], published);
      void queryClient.invalidateQueries({ queryKey: [VERSIONS_KEY, tenantKey] });
    },
  });
}

/**
 * Server-side what-if (POST /policy/what-if, OC-3b). Variables: candidate
 * LiquidityPolicyParameters, or null to evaluate the currently effective
 * policy. The same server scorecard computes previews and production
 * recommendations -- there is no client-side scorecard math. Errors carry
 * `status` and `code`; the panel maps the 404 family (POLICY_NOT_FOUND,
 * NO_SCORED_ACCOUNTS, the flag-off stealth) to named empty states and
 * renders 400 POLICY_SEMANTIC_VALIDATION_FAILED details like the publish
 * Alert does.
 */
export function useLimitsWhatIf() {
  return useMutation<
    LimitsDistribution,
    Error & { details?: { path?: string; issue?: string; message?: string }[]; status?: number; code?: string },
    LiquidityPolicyParameters | null
  >({
    mutationFn: async (parameters: LiquidityPolicyParameters | null) => {
      const { data, error, response } = await memberLiquidityClient.POST(
        '/api/v1/tempo/policy/what-if',
        { body: parameters ? { parameters } : {} },
      );
      if (error || !data) throw toApiError(error, response);
      return data;
    },
  });
}

/**
 * One page of a domain's immutable version history (newest first).
 * `cursor` is the nextCursor from the prior page; undefined = first page.
 */
export function usePolicyVersions(domain: PolicyDomain, cursor?: string) {
  const tenantKey = useTenantCacheKey();
  return useQuery<PolicyVersionPage, Error>({
    queryKey: [VERSIONS_KEY, tenantKey, domain, cursor ?? ''],
    queryFn: async () => {
      const { data, error, response } = await memberLiquidityClient.GET(
        '/api/v1/tempo/policy/versions',
        { params: { query: { domain, ...(cursor ? { cursor } : {}) } } },
      );
      // Flag-off stealth 404: render as an empty history rather than an error.
      if (response.status === 404) return { items: [] };
      if (error || !data) throw toApiError(error, response);
      return data;
    },
    retry: false,
  });
}

/**
 * Examiner-evidence download. Fetches the typed export and hands it to the
 * browser as a JSON attachment. The integrityHash re-derives from the
 * parsed file's `{formatVersion, versions}` envelope (canonical key-sorted
 * JSON, SHA-256; formatVersion inside the digest input since 2026-08-30),
 * so re-serialization here does not affect verification.
 */
export function useDownloadPolicyEvidence() {
  return useMutation<PolicyEvidenceExport, Error & { status?: number }, PolicyDomain>({
    mutationFn: async (domain: PolicyDomain) => {
      const { data, error, response } = await memberLiquidityClient.GET(
        '/api/v1/tempo/policy/evidence',
        { params: { query: { domain } } },
      );
      if (error || !data) throw toApiError(error, response);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `policy-evidence-${domain.toLowerCase()}-${data.exportedAt.slice(0, 10)}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      return data;
    },
  });
}
